import { Helmet } from 'react-helmet-async';
import { Box, Button, Card, CardContent, Grid, Stack, Typography, Alert, LinearProgress } from '@mui/material';
import React, { useState } from 'react';
import { PATH_DASHBOARD } from '../../routes/paths';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { uploadFirmware } from '../../api';
import { useSnackbar } from 'src/components/snackbar';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from '../../components/hook-form';
import { fData } from '../../utils/formatNumber';
import Iconify from '../../components/iconify';

export default function OTAManageUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const defaultValues = {
    version: '',
    hardware_version: '',
    expected_checksum: '',
    release_notes: '',
  };

  const methods = useForm({
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // 检查文件大小 (500MB)
      if (selectedFile.size > 500 * 1024 * 1024) {
        enqueueSnackbar('文件大小不能超过500MB！', { variant: 'error' });
        return;
      }
      // 检查文件格式
      if (!selectedFile.name.endsWith('.bin')) {
        enqueueSnackbar('请选择.bin格式的固件文件！', { variant: 'error' });
        return;
      }
      setFile(selectedFile);
    }
  };

  const onSubmit = async (data: any) => {
    if (!file) {
      enqueueSnackbar('请选择固件文件！', { variant: 'error' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('firmware', file);
      formData.append('version', data.version);
      formData.append('hardware_version', data.hardware_version);
      formData.append('expected_checksum', data.expected_checksum);
      formData.append('release_notes', data.release_notes || '');

      // 模拟上传进度
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const res: any = await uploadFirmware(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (res.code === 200) {
        enqueueSnackbar('固件上传成功！');
        navigate(PATH_DASHBOARD.otaManage.list);
      } else {
        enqueueSnackbar(res.message || '上传失败！', { variant: 'error' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      enqueueSnackbar('上传失败，请重试！', { variant: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <Helmet>
        <title>上传固件</title>
      </Helmet>

      <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
        上传固件
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <RHFTextField
                      name="version"
                      label="软件版本号"
                      placeholder="例如: YZ.NVT.01.SV.A.01.02.000002"
                      required
                      helperText="请使用标准版本号格式，如：YZ.NVT.01.SV.A.01.02.000002"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <RHFTextField
                      name="hardware_version"
                      label="硬件版本号"
                      placeholder="例如: YZ.NVT.HV.R.10.10.ABC"
                      required
                      helperText="用于设备兼容性检查，如：YZ.NVT.HV.R.10.10.ABC"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <RHFTextField
                      name="expected_checksum"
                      label="SHA256 校验和"
                      placeholder="例如: a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z6a7b8c9d0e1f"
                      required
                      helperText="固件文件的 SHA256 校验和（64位十六进制字符），用于验证文件完整性"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <RHFTextField
                      name="release_notes"
                      label="发布说明"
                      multiline
                      rows={3}
                      placeholder="描述此版本的主要更新内容..."
                    />
                  </Grid>


                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => document.getElementById('file-input')?.click()}
                    >
                      <input
                        id="file-input"
                        type="file"
                        accept=".bin"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      {file ? (
                        <Stack spacing={2} alignItems="center">
                          <Iconify icon="eva:file-fill" width={48} />
                          <Typography variant="h6">{file.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {fData(file.size)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack spacing={2} alignItems="center">
                          <Iconify icon="eva:cloud-upload-fill" width={48} />
                          <Typography variant="h6">点击选择固件文件</Typography>
                          <Typography variant="body2" color="text.secondary">
                            支持.bin格式，最大500MB
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Grid>

                  {uploading && (
                    <Grid item xs={12}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          上传进度: {uploadProgress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={uploading || !file || isSubmitting}
                      >
                        {uploading ? '上传中...' : '上传固件'}
                      </Button>
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to={PATH_DASHBOARD.otaManage.list}
                        disabled={uploading}
                      >
                        取消
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </FormProvider>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                上传说明
              </Typography>
              <Stack spacing={2}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>文件要求：</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 文件格式：.bin
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 文件大小：最大500MB
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 版本号：使用语义化版本号
                  </Typography>
                  <Typography variant="body2" component="div">
                    • SHA256校验和：64位十六进制字符
                  </Typography>
                </Alert>

                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>获取 SHA256 校验和：</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Windows: certutil -hashfile 文件名.bin SHA256
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Mac/Linux: shasum -a 256 文件名.bin
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 或使用: sha256sum 文件名.bin
                  </Typography>
                </Alert>

                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>注意事项：</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 确保固件文件完整且未损坏
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 硬件版本号必须与目标设备匹配
                  </Typography>
                  <Typography variant="body2" component="div">
                    • SHA256校验和必须准确，用于验证文件完整性
                  </Typography>
                </Alert>

                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>上传后：</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 系统会验证文件SHA256校验和
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 校验和不匹配时会拒绝上传
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 文件按版本号分目录存储
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 可在固件列表中查看和管理
                  </Typography>
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
